/**
 * Wave 2b PBR completion for the art-backed hulls.
 *
 * The `-dec` hulls already ship metal/rough `MeshStandardMaterial`s (physically-based
 * enough for painted metal). The one place a fuller BSDF *buys* realism is the CANOPY
 * GLASS: a `MeshPhysicalMaterial` clearcoat layer gives the wet, lacquered second
 * specular lobe that reads as a curved glass canopy under the sun. Per the wave brief,
 * **clearcoat is applied ONLY to canopy glass** — the rest of the hull stays on the
 * cheaper standard material (upgrading opaque painted panels to physical buys nothing
 * but VRAM + a heavier shader).
 *
 * Canopy materials are identified by name (the GLB authoring convention names them
 * `canopy` / `glass` / `cockpit` / `window` / `visor`). When no material matches,
 * every call here is a no-op — so a hull without a named canopy is untouched.
 */
import * as THREE from 'three';

const CANOPY_KEYWORDS = ['canopy', 'glass', 'cockpit', 'window', 'visor', 'windshield'];

/** True when a material's name marks it as canopy glass (case-insensitive). */
export const isCanopyGlass = (material: THREE.Material): boolean => {
  const name = material.name.toLowerCase();
  return CANOPY_KEYWORDS.some((keyword) => name.includes(keyword));
};

/**
 * Build a clearcoat `MeshPhysicalMaterial` from a standard canopy material, copying
 * the maps/tint/roughness across explicitly (safer than `.copy()`, which would pull
 * undefined physical fields off a standard source). Clearcoat only — no transmission,
 * so the canopy stays cheap (no transmission render target) while gaining the glossy
 * curved-glass lobe. The base layer keeps its dark matte tint (aesthetic gate: the
 * memorial palette stays restrained; the clearcoat is a specular sheen, not a glow).
 */
export const toClearcoatGlass = (
  std: THREE.MeshStandardMaterial,
): THREE.MeshPhysicalMaterial => {
  const glass = new THREE.MeshPhysicalMaterial({
    color: std.color.clone(),
    map: std.map,
    normalMap: std.normalMap,
    normalScale: std.normalScale.clone(),
    roughnessMap: std.roughnessMap,
    metalnessMap: std.metalnessMap,
    roughness: std.roughness,
    metalness: std.metalness,
    envMapIntensity: std.envMapIntensity,
    transparent: std.transparent,
    opacity: std.opacity,
    side: std.side,
    clearcoat: 1.0,
    clearcoatRoughness: 0.08,
    ior: 1.45,
  });
  glass.name = std.name;
  return glass;
};

/** Swap one material in place if it is canopy glass; otherwise return it unchanged. */
const upgradeMaterial = (material: THREE.Material): THREE.Material => {
  if (!(material instanceof THREE.MeshStandardMaterial)) return material;
  if (material instanceof THREE.MeshPhysicalMaterial) return material; // already upgraded
  if (!isCanopyGlass(material)) return material;
  return toClearcoatGlass(material);
};

/**
 * Traverse a loaded hull and replace canopy-glass standard materials with clearcoat
 * physical ones in place. Handles multi-material meshes. No-op for hulls with no
 * named canopy material, so it is safe to call on every hull unconditionally.
 */
export const upgradeHullMaterials = (root: THREE.Object3D): void => {
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    if (Array.isArray(object.material)) {
      object.material = object.material.map(upgradeMaterial);
      return;
    }
    object.material = upgradeMaterial(object.material);
  });
};
